import { Fragment, useLayoutEffect, useMemo, useRef } from "react";
import cn from "classnames";
import Styles from "./SchedulePreview.module.scss";
import { Text } from "src/components/atoms/Text/Text";
import { Spinner } from "src/components/atoms/Spinner";
import { useGetSearchTeamScheduleQuery } from "src/state/modules/api";
import { DATE_FORMAT, formatDate } from "src/utils/date-utils";
import * as Eden from "src/eden/content";

interface SchedulePreviewProps {
  teamId: string;
}

type ResultTone = "win" | "loss" | "tie" | "live" | "upcoming";

const resultFor = (game: Eden.SchedulePreviewGame): { label: string; tone: ResultTone } => {
  const { game_status, team_score, opponent_score } = game;
  const hasScore = team_score != null && opponent_score != null;
  const score = hasScore ? `${team_score}-${opponent_score}` : "";

  if (game_status === "live" || game_status === "new") {
    return { label: hasScore ? `LIVE ${score}` : "LIVE", tone: "live" };
  }
  if (game_status === "completed" && hasScore) {
    if (team_score! > opponent_score!) return { label: `W ${score}`, tone: "win" };
    if (team_score! < opponent_score!) return { label: `L ${score}`, tone: "loss" };
    return { label: `T ${score}`, tone: "tie" };
  }
  return { label: game.status === "canceled" ? "Canceled" : "Upcoming", tone: "upcoming" };
};

// Game dates from GameChanger are stored at UTC midnight for date-only events; format in UTC
// so they don't roll back a day in western timezones.
const gameDate = (start: string | null) => (start ? formatDate(start, "UTC", DATE_FORMAT.monthDayAndYear) : "TBD");

const todayString = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};

export const SchedulePreview = ({ teamId }: SchedulePreviewProps) => {
  const { data, isLoading, isError } = useGetSearchTeamScheduleQuery(teamId);

  const listRef = useRef<HTMLDivElement>(null);
  const boundaryRef = useRef<HTMLDivElement>(null);

  // Index of the first game that is today or in the future. Everything before it has been
  // played; from it onward is the team's upcoming schedule. Dates are compared as YYYY-MM-DD
  // strings to sidestep timezone conversion (GC stores date-only games at UTC midnight).
  const firstUpcomingIndex = useMemo(() => {
    if (!data) return -1;
    const today = todayString();
    return data.findIndex((game) => (game.start ?? "").slice(0, 10) >= today);
  }, [data]);

  // On expand, scroll the panel to "now": pin the last finished game to the top so the upcoming
  // games sit right below it. This is the main thing the user is checking ("are they my next
  // opponent?"), so their next games are front and center.
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list || !data || data.length === 0) return;
    if (firstUpcomingIndex === -1) {
      list.scrollTop = list.scrollHeight; // all games are in the past — show the most recent
    } else if (firstUpcomingIndex === 0) {
      list.scrollTop = 0; // every game is upcoming
    } else if (boundaryRef.current) {
      list.scrollTop = boundaryRef.current.offsetTop;
    }
  }, [data, firstUpcomingIndex]);

  if (isLoading) {
    return (
      <div className={Styles.state}>
        <Spinner label="Loading schedule…" />
      </div>
    );
  }

  if (isError) {
    return (
      <Text className={Styles.state} size="small" color="cool-grey-dark" block>
        Couldn't load this team's schedule.
      </Text>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Text className={Styles.state} size="small" color="cool-grey-dark" block>
        No games found for this team.
      </Text>
    );
  }

  return (
    <div className={Styles.wrapper}>
      <div className={Styles.list} ref={listRef}>
        {data.map((game, index) => {
          const result = resultFor(game);
          const prefix = game.home_away === "away" ? "@" : "vs";
          const showDivider = index === firstUpcomingIndex && firstUpcomingIndex > 0;
          const isLastFinished = index === firstUpcomingIndex - 1;
          return (
            <Fragment key={game.event_id}>
              {showDivider && (
                <div className={Styles.divider}>
                  <span>Upcoming</span>
                </div>
              )}
              <div className={Styles.row} ref={isLastFinished ? boundaryRef : undefined}>
                <Text className={Styles.date} size="xsmall" color="cool-grey-dark" noWrap>
                  {gameDate(game.start)}
                </Text>
                <Text className={Styles.matchup} size="small" color="off-black">
                  {`${prefix} ${game.opponent_name ?? "Unknown opponent"}`}
                </Text>
                <span className={cn(Styles.result, Styles[result.tone])}>{result.label}</span>
              </div>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
};
